import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Rule, ScheduleType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule.pb';
import dayjs from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { TFunction, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  Chip,
  ConfirmationModal,
  Table,
  TableColumnConfig,
  Text,
  emptyStateGraphics,
  useAlert,
  useModalControl,
} from '@frontend/design-system';
import { CompactTable } from '../../components/compact-table';
import { DirectAvailabilityCheck } from '../../components/DirectAvailabilityCheck';
import {
  DEFAULT_EXCEPTION_FILTER,
  PractitionerExceptionTableFilter,
} from '../../components/PractitionerExceptionTableFilter';
import { ScheduleBanner } from '../../components/ScheduleBanner';
import { TableHeaderWithTimezoneInfo } from '../../components/v3/table-header-with-timezone-info';
import { scheduleDirectAvailabilityBannerText } from '../../constants';
import { useScheduleSettingsSmallMode } from '../../hooks/settings/use-schedule-settings-small-mode';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { getDateRangeFromRRule } from '../../rrule-helper';
import { ExceptionStatus, ExceptionListItem, ExceptionTableFilter } from '../../types';
import {
  findOverrideIndexById,
  getFormattedExceptionDateRange,
  getFormattedExceptionTimeRange,
  isPastException,
  isUpcomingException,
} from '../../utils';
import { CreateExceptionModal } from './CreateExceptionModal';

const { providerScheduleDirectAvailabilityTitle, providerScheduleDirectAvailabilityMessage } =
  scheduleDirectAvailabilityBannerText;

type ExceptionsV3PageProps = {
  entityId: string;
  locationId: string;
  entityName: string;
  entityType: ScheduleType.PROVIDER | ScheduleType.LOCATION;
};

export const ExceptionsV3Page = ({ entityId, locationId, entityName, entityType }: ExceptionsV3PageProps) => {
  const [selectedFilterValue, setSelectedFilterValue] = useState<ExceptionTableFilter>(DEFAULT_EXCEPTION_FILTER);
  const [selectedExceptionInfo, setSelectedExceptionInfo] = useState<
    | {
        item: ExceptionListItem;
        type: 'edit' | 'delete';
      }
    | undefined
  >();
  const { t } = useTranslation('schedule-settings');
  const alert = useAlert();
  const { getLocationTimeZone } = useSchedulingLocationInfo();
  const locationTimeZone = getLocationTimeZone(locationId);
  const { isSmallMode } = useScheduleSettingsSmallMode();

  const entityTypeName = entityType === ScheduleType.PROVIDER ? t('Provider') : t('Office');

  // Custom modal control to reset selected exception on closing modal
  const customModalControl = { onClose: () => setSelectedExceptionInfo(undefined) };

  const { modalProps: deleteModalProps, triggerProps: deleteTriggerProps } = useModalControl(customModalControl);
  const { modalProps: editModalProps, triggerProps: editTriggerProps } = useModalControl(customModalControl);

  const deleteScheduleMutation = SchedulerV3.Mutations.useUpdateScheduleMutation({
    options: {
      onSuccess: () => {
        alert.success(t('{{entityTypeName}} exceptions deleted successfully', { entityTypeName }));
      },
      onError: () => {
        alert.error(t('Failed to delete {{entityTypeName}} exceptions', { entityTypeName }));
      },
    },
  });

  const listSchedulesQuery = SchedulerV3.Queries.useListSchedulesQuery({
    request: {
      locationId,
      ids: [entityId],
    },
    options: {
      enabled: !!locationId && !!entityId,
    },
  });

  const deleteException = () => {
    const entitySchedule = listSchedulesQuery.data?.schedules?.[0];
    if (!entitySchedule) return;

    const { availabilities = [], unAvailabilities = [] } = entitySchedule.recurrenceRules?.override || {};

    const updateOverrides = (overrides: Rule[]) => {
      const overrideIndex = findOverrideIndexById(overrides, selectedExceptionInfo?.item?.id ?? '');
      if (overrideIndex !== -1) {
        overrides.splice(overrideIndex, 1);
      }
    };

    if (selectedExceptionInfo?.item?.status === ExceptionStatus.Available) {
      updateOverrides(availabilities);
    } else if (selectedExceptionInfo?.item?.status === ExceptionStatus.Unavailable) {
      updateOverrides(unAvailabilities);
    }

    deleteScheduleMutation.mutate({
      id: entitySchedule.id,
      locationId,
      type: entitySchedule.type || entityType,
      recurrenceRules: {
        ...entitySchedule.recurrenceRules,
        override: { availabilities, unAvailabilities },
      },
    });
  };

  const handleDeleteModal = useCallback(
    (exception: ExceptionListItem) => {
      setSelectedExceptionInfo({ item: exception, type: 'delete' });
      deleteTriggerProps.onClick();
    },
    [deleteTriggerProps]
  );

  const handleEditModal = useCallback(
    (exception: ExceptionListItem) => {
      setSelectedExceptionInfo({ item: exception, type: 'edit' });
      editTriggerProps.onClick();
    },
    [editTriggerProps]
  );

  const tableColumns = useMemo(
    (): TableColumnConfig<ExceptionListItem>[] => [
      {
        Header: t('Name of Exception'),
        id: 'exceptionName',
        accessor: (row) => row.name,
      },
      {
        Header: t('Availability'),
        id: 'availability',
        accessor: (row) => row.status,
        cellRenderer: (status: ExceptionStatus) => {
          return <Chip variant={status === ExceptionStatus.Available ? 'primary' : 'warn'}>{status}</Chip>;
        },
      },
      {
        Header: <TableHeaderWithTimezoneInfo title={t('Date')} locationId={locationId} />,
        id: 'date',
        accessor: (row) => getFormattedExceptionDateRange(row.startDateTime, row.endDateTime),
        minWidth: 320,
        maxWidth: 360,
        // startingSortBy is commented out temporarily while it's triggering an infinite loop and crashing the page.
        // startingSortBy: 'asc',
        sortType: (rowA, rowB) => {
          if (
            rowA.original.startDateTime &&
            rowB.original.startDateTime &&
            rowA.original.startDateTime < rowB.original.startDateTime
          ) {
            return -1;
          }
          return 0;
        },
      },
      {
        Header: <TableHeaderWithTimezoneInfo title={t('Time')} locationId={locationId} />,
        id: 'time',
        accessor: (row) => getTimeText(row, t),
        disableSortBy: true,
      },
    ],
    [locationId, t]
  );

  const tableRowActions = useMemo(
    () => [
      {
        Icon: () => <Icon name='edit' />,
        label: t('Edit'),
        onClick: (exception: ExceptionListItem) => handleEditModal(exception),
      },
      {
        Icon: () => <Icon name='trash' />,
        label: t('Delete'),
        onClick: (exception: ExceptionListItem) => handleDeleteModal(exception),
      },
    ],
    [handleEditModal, handleDeleteModal, t]
  );

  const tableActions = useMemo(
    () => [
      {
        type: 'button' as const,
        variant: 'primary' as const,
        label: t('New Exception'),
        onClick: () => editTriggerProps.onClick(),
      },
    ],
    [editTriggerProps, t]
  );

  // Local list of exceptions from overrides object
  const exceptionList = useMemo(() => {
    const overrides = listSchedulesQuery.data?.schedules?.[0]?.recurrenceRules?.override;
    if (!overrides) return [];

    const list: ExceptionListItem[] = [];

    const createExceptionListItems = (override: Rule, status: ExceptionStatus) => {
      const name = override.name || '';
      const duration = override.duration || 0;
      const id = override.id || '';
      const { startDateTime, endDateTime } = getDateRangeFromRRule(override.rrule || '');

      list.push({ id, name, status, startDateTime, endDateTime, duration });
    };

    const overrideAvailabilities = overrides?.availabilities || [];
    const overrideUnAvailabilities = overrides?.unAvailabilities || [];

    overrideAvailabilities?.forEach((override: Rule) => {
      createExceptionListItems(override, ExceptionStatus.Available);
    });

    overrideUnAvailabilities?.forEach((override: Rule) => {
      createExceptionListItems(override, ExceptionStatus.Unavailable);
    });

    // Default sort by startDatetime descending
    return list.sort((a, b) => (a.startDateTime && b.startDateTime && a.startDateTime < b.startDateTime ? -1 : 1));
  }, [listSchedulesQuery.data]);

  const filteredExceptionList = useMemo(() => {
    return exceptionList.filter((exception: ExceptionListItem) => {
      if (!exception.startDateTime || !exception.endDateTime) return true;

      if (selectedFilterValue === ExceptionTableFilter.Upcoming) {
        return isUpcomingException(dayjs.tz(exception.endDateTime, locationTimeZone).toDate());
      }

      if (selectedFilterValue === ExceptionTableFilter.Past) {
        const isPast = isPastException(dayjs.tz(exception.startDateTime, locationTimeZone).toDate());
        return isPast;
      }

      return true;
    });
  }, [exceptionList, locationTimeZone, selectedFilterValue]);

  const tableProps = {
    isLoading: listSchedulesQuery.isLoading || deleteScheduleMutation.isLoading,
    data: filteredExceptionList,
    rowActions: { actions: tableRowActions },
    tableActions: tableActions,
    emptyStateConfig: {
      type: 'schedule_alerts' as keyof typeof emptyStateGraphics,
      header: '',
      description:
        selectedFilterValue === ExceptionTableFilter.Past
          ? t('Clean slate: No previous schedule exceptions recorded')
          : t('This {{entityTypeName}} is really sticking to their schedule - no exceptions added so far!', {
              entityTypeName,
            }),
    },
  };

  return (
    <div css={{ paddingTop: '36px' }}>
      {entityType === ScheduleType.PROVIDER && (
        <DirectAvailabilityCheck locationId={locationId}>
          <ScheduleBanner
            fullWidth
            title={providerScheduleDirectAvailabilityTitle}
            message={providerScheduleDirectAvailabilityMessage}
          />
        </DirectAvailabilityCheck>
      )}
      {isSmallMode ? (
        <CompactTable
          {...tableProps}
          tableId='manage-entity-exceptions-compact-table'
          rowElement={(exception: ExceptionListItem) => <RowHeader exception={exception} />}
          expandedRowElement={(exception: ExceptionListItem) => <ExpandedRowElement exception={exception} />}
          toolbarElement={<Toolbar setSelectedFilterValue={setSelectedFilterValue} />}
          styleConfig={{ toolbar: styles.toolbar }}
        />
      ) : (
        <Table
          {...tableProps}
          isPaginated
          colConfig={tableColumns}
          hasGlobalSearch
          customToolbarRender={() => <PractitionerExceptionTableFilter setValue={setSelectedFilterValue} />}
          tableStyle={{
            name: 'manage-entity-exceptions-v3-table',
            styles: `margin-bottom: ${theme.spacing(2)};`,
          }}
          wrapperStyle={css`
            .table-toolbar {
              justify-content: space-between;
              gap: ${theme.spacing(2)};
            }
          `}
        />
      )}
      <ConfirmationModal
        {...deleteModalProps}
        title={t('Delete Exception')}
        destructive
        message={t(
          "Are you sure you want to delete {{exceptionName}}? Doing so will remove this exception from {{entityName}}'s schedule, and their availability will be updated accordingly.",
          { entityName: entityName, exceptionName: selectedExceptionInfo?.item?.name ?? '' }
        )}
        confirmLabel={t('Delete')}
        cancelLabel={t('Cancel')}
        onCancel={deleteModalProps.onClose}
        onConfirm={deleteException}
      />
      <CreateExceptionModal
        modalProps={editModalProps}
        locationId={locationId}
        entityId={entityId}
        entitySchedule={listSchedulesQuery.data?.schedules?.[0]}
        exceptionDetails={selectedExceptionInfo?.type === 'edit' ? selectedExceptionInfo.item : undefined}
        entityType={entityType}
      />
    </div>
  );
};

const Toolbar = ({
  setSelectedFilterValue,
}: {
  setSelectedFilterValue: Dispatch<SetStateAction<ExceptionTableFilter>>;
}) => {
  return (
    <div>
      <div css={styles.filterContainer}>
        <CompactTable.TableActions />
        <PractitionerExceptionTableFilter setValue={setSelectedFilterValue} />
      </div>
      <div css={styles.expandAllContainer}>
        <CompactTable.ExpandAll />
      </div>
    </div>
  );
};

const RowHeader = ({ exception }: { exception: ExceptionListItem }) => {
  return (
    <div>
      <CompactTable.RowHeader title={exception.name} />
      <div css={styles.rowHeaderSecondRow}>
        <Text size='medium'>{getFormattedExceptionDateRange(exception.startDateTime, exception.endDateTime)}</Text>
        <Chip variant={exception.status === ExceptionStatus.Available ? 'primary' : 'warn'}>{exception.status}</Chip>
      </div>
    </div>
  );
};

const getTimeText = (exception: ExceptionListItem, t: TFunction) => {
  const timeText = getFormattedExceptionTimeRange(exception.startDateTime, exception.duration);
  return timeText === 'All Day' ? t('All Day') : timeText;
};

const ExpandedRowElement = ({ exception }: { exception: ExceptionListItem }) => {
  const { t } = useTranslation('schedule-settings');

  return (
    <CompactTable.ExpandedRow>
      <CompactTable.InfoRow label={t('Time')} value={getTimeText(exception, t)} />
    </CompactTable.ExpandedRow>
  );
};

const styles = {
  rowHeaderSecondRow: css`
    display: flex;
    gap: ${theme.spacing(1)};
    justify-content: space-between;
    padding: ${theme.spacing(0, 1, 1)};
  `,
  toolbar: css`
    margin-top: 0;
  `,
  filterContainer: css`
    display: flex;
    justify-content: space-between;
    gap: ${theme.spacing(2)};
    flex-wrap: wrap;
  `,
  expandAllContainer: css`
    width: 100%;
    display: flex;
    justify-content: flex-end;
    padding-top: ${theme.spacing(1)};
  `,
};
